import { useQuery } from '@apollo/client';
import TextField from '@mui/material/TextField';
import * as Sentry from '@sentry/react';
import cx from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useSearchParams } from 'react-router';

import { gengql } from '@/__generated__';
import { ConsultationState, Query } from '@/__generated__/graphql';
import { notify } from '@/actions/ui';
import Button from '@/components/Button/Button';
import ConsultationList from '@/components/ConsultationList';
import EditExternalConsultation from '@/components/EditExternalConsultation';
import EmptyMessage from '@/components/EmptyMessage';
import FAIcon from '@/components/Icon/FAIcon';
import MaterialIcon from '@/components/Icon/MaterialIcon';
import { Select } from '@/components/Select/Select';
import { isReviewable } from '@/consultation/reviewWindow';
import { canReviewConsultation } from '@/consultation/reviewer';
import { fetchExpertRequests } from '@/expertrequest/store';
import { RootState } from '@/store';
import { normalizeSpace } from '@/utils';

const FETCH_CONSULTATIONS = gengql(/* GraphQL */ `
  query fetchConsultations(
    $cursor: String
    $all: Boolean
    $states: [ConsultationState]
    $pageSize: Int!
    $userContext: String!
    $sort: String
    $sortOrder: String
    $searchTitle: String
  ) {
    searchConsultations(
      after: $cursor
      all: $all
      states: $states
      first: $pageSize
      user_context: $userContext
      sort: $sort
      sort_order: $sortOrder
      search_title: $searchTitle
    ) {
      pageInfo {
        hasNextPage
        total
      }
      edges {
        cursor
        node {
          id
          html_url
          external
          state
          starts_at
          billing_duration
          recording_duration
          archived
          engagement_type
          permissions
          requester {
            id
            username
            name
            first_name
            last_name
            html_url
            picture_url
          }
          requester_name
          expert {
            id
            username
            name
            first_name
            last_name
            html_url
            picture_url
          }
          expert_name
          expert_request {
            id
            name
          }
          expert_review {
            id
          }
        }
      }
    }
  }
`);

const STATE_MAP: Record<string, ConsultationState[]> = {
  AWAITING: [ConsultationState.NegotiatingExpertTime, ConsultationState.NegotiatingClientTime],
  UPCOMING: [
    ConsultationState.Confirmed,
    ConsultationState.AwaitingJoin,
    ConsultationState.AwaitingClientJoin,
    ConsultationState.AwaitingExpertJoin,
    ConsultationState.AwaitingExpertReview,
    ConsultationState.AwaitingClientAccept,
    ConsultationState.InProgress,
    ConsultationState.Finalizing,
  ],
  COMPLETED: [ConsultationState.Completed],
  'EXPIRED/CANCELED': [
    ConsultationState.Canceled,
    ConsultationState.Denied,
    ConsultationState.ClientRejected,
    ConsultationState.Expired,
    ConsultationState.Incomplete,
  ],
};

const STATE_TO_EMPTY_TEXT: Record<string, string> = {
  ALL: 'You don\u2019t have any calls.',
  AWAITING: 'You don\u2019t have any awaiting calls.',
  UPCOMING: 'You don\u2019t have any upcoming calls.',
  COMPLETED: 'You don\u2019t have any completed calls.',
  'EXPIRED/CANCELED': 'You don\u2019t have any expired or canceled calls.',
  NEEDS_REVIEW: 'You don\u2019t have any calls waiting for your review.',
  NEEDS_REVIEW_MORE: 'None of the calls loaded so far are waiting for your review.',
  ERROR: 'We couldn\u2019t load your calls.',
};

const STATE_TO_TITLE_MAP: Record<string, string> = {
  AWAITING: 'Awaiting',
  UPCOMING: 'Upcoming',
  COMPLETED: 'Completed',
  'EXPIRED/CANCELED': 'Expired/Canceled',
};

const STAT_META: Record<string, { label: string; icon: string; accent: string }> = {
  AWAITING: { label: 'Awaiting', icon: 'schedule', accent: 'text-yellow-600 dark:text-yellow-400' },
  UPCOMING: { label: 'Upcoming', icon: 'event', accent: 'text-teal-700 dark:text-teal-400' },
  COMPLETED: {
    label: 'Completed',
    icon: 'check_circle',
    accent: 'text-green-600 dark:text-green-400',
  },
  'EXPIRED/CANCELED': { label: 'Expired', icon: 'cancel', accent: 'text-on-surface-muted' },
};

interface ConsultationPromoProps {
  status: string;
  action?: React.ReactNode;
}

const ConsultationPromo = ({ status, action }: ConsultationPromoProps) => {
  return (
    <EmptyMessage
      style={{ marginTop: 10 }}
      icon={<FAIcon icon="frown-o" className="text-5xl text-on-surface-muted" />}
      title={STATE_TO_EMPTY_TEXT[status]}
      body=""
      action={action}
    />
  );
};

const connector = connect(
  (state: RootState) => ({
    userContext: state.ui.userContext,
    viewer: state.viewer,
  }),
  {
    fetchExpertRequests,
    notify,
  }
);

const Consultations = ({
  viewer,
  userContext,
  fetchExpertRequests,
  notify,
}: ConnectedProps<typeof connector>) => {
  const useFetchConsultations = (state: string, userContext: string, searchTitle: string) => {
    return useQuery(FETCH_CONSULTATIONS, {
      variables: {
        states: STATE_MAP[state],
        pageSize: 10,
        all: true,
        sort: 'created_at',
        sortOrder: 'desc',
        userContext: userContext,
        searchTitle: searchTitle,
      },
    });
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const needsReview = searchParams.get('filter') === 'needs_review';

  const [erLoading, setErLoading] = useState(false);
  const [externalConsultationOpen, setExternalConsultationOpen] = useState(false);
  const [searchTitle, setSearchTitle] = useState('');
  const [status, setStatus] = useState(needsReview ? 'COMPLETED' : 'ALL');

  const toggleNeedsReview = useCallback(() => {
    setSearchParams((prev: URLSearchParams) => {
      const next = new URLSearchParams(prev);
      if (needsReview) {
        next.delete('filter');
      } else {
        next.set('filter', 'needs_review');
      }
      return next;
    });
    if (!needsReview) setStatus('COMPLETED');
  }, [needsReview, setSearchParams]);

  const awaitingConsultations = useFetchConsultations('AWAITING', userContext, searchTitle);
  const upcomingConsultations = useFetchConsultations('UPCOMING', userContext, searchTitle);
  const completedConsultations = useFetchConsultations('COMPLETED', userContext, searchTitle);
  const expiredConsultations = useFetchConsultations('EXPIRED/CANCELED', userContext, searchTitle);

  type ConsultationStatus = 'AWAITING' | 'UPCOMING' | 'COMPLETED' | 'EXPIRED/CANCELED';

  const dataSet: Record<ConsultationStatus, any> = useMemo(
    () => ({
      AWAITING: awaitingConsultations,
      UPCOMING: upcomingConsultations,
      COMPLETED: completedConsultations,
      'EXPIRED/CANCELED': expiredConsultations,
    }),
    [awaitingConsultations, upcomingConsultations, completedConsultations, expiredConsultations]
  );

  // fetchMore and refetch never flip `loading` (no notifyOnNetworkStatusChange), so track
  // them here. That drives the spinners, and stops a double click fetching one cursor twice.
  const inFlight = useRef(new Set<ConsultationStatus>());
  const [fetchingMore, setFetchingMore] = useState<ConsultationStatus[]>([]);
  const [retrying, setRetrying] = useState(false);

  const handleMore = useCallback(
    (state: ConsultationStatus) => {
      const { fetchMore, data } = dataSet[state];
      if (inFlight.current.has(state)) return;
      if (!data.searchConsultations.pageInfo.hasNextPage) return;
      if (!data.searchConsultations.edges.length)
        throw new Error('last consultation edge not found');
      inFlight.current.add(state);
      setFetchingMore((prev) => [...prev, state]);
      fetchMore({
        variables: {
          cursor: data.searchConsultations.edges[data.searchConsultations.edges.length - 1].cursor,
        },
        updateQuery: (previousResult: Query, { fetchMoreResult }: { fetchMoreResult: Query }) => {
          if (!fetchMoreResult) return previousResult;
          return {
            searchConsultations: {
              pageInfo: fetchMoreResult.searchConsultations.pageInfo,
              edges: [
                ...previousResult.searchConsultations.edges,
                ...fetchMoreResult.searchConsultations.edges,
              ],
            },
          };
        },
      })
        .catch((error: unknown) => {
          notify('We couldn\u2019t load more calls. Try again.', 'error');
          Sentry.captureException(error, {
            level: 'warning',
            tags: { consultations_load_more_failed: 'true' },
          });
        })
        .finally(() => {
          inFlight.current.delete(state);
          setFetchingMore((prev) => prev.filter((s) => s !== state));
        });
    },
    [dataSet, notify]
  );

  const handleExternalConsultationDialog = useCallback(async () => {
    try {
      setErLoading(true);
      await fetchExpertRequests({ state: 'open' });
      setExternalConsultationOpen(true);
    } finally {
      setErLoading(false);
    }
  }, [fetchExpertRequests]);

  const closeExternalConsultation = useCallback(() => {
    setExternalConsultationOpen(false);
  }, []);

  const belongsToEnterpriseGroup =
    (viewer.groups || []).filter((g) => g.account_type === 'enterprise')?.length > 0;

  const filterEdges = useCallback(
    (edges: any[] | undefined) => {
      if (!edges) return edges;
      if (!needsReview) return edges;
      return edges.filter(
        (edge: any) =>
          !edge?.node?.expert_review &&
          isReviewable(edge?.node?.starts_at) &&
          canReviewConsultation(viewer.id, edge?.node)
      );
    },
    [needsReview, viewer.id]
  );

  // A status count is the server-side total for that status (ENG-5253), so it stays
  // the same as more pages load. Needs-review only narrows which loaded completed rows
  // are rendered and has no server-side count, so it must not swap the Completed total
  // for the length of the loaded, filtered pages. Undefined until the status has
  // loaded, so no "0" flashes before the real number. ENG-5261 (FB-82).
  const getConsultationTotal = (state: ConsultationStatus): number | undefined =>
    dataSet[state].data?.searchConsultations?.pageInfo?.total ?? undefined;
  const allConsultationsCount = Object.keys(dataSet).reduce(
    (acc, key) => acc + (getConsultationTotal(key as ConsultationStatus) ?? 0),
    0
  );
  const allLoading = Object.keys(dataSet).some((key) => dataSet[key as ConsultationStatus].loading);
  // A failed status has no data, which would otherwise read as "no calls".
  const erroredStates = (Object.keys(dataSet) as ConsultationStatus[]).filter(
    (key) => dataSet[key].error
  );
  const handleRetry = async () => {
    setRetrying(true);
    // allSettled: a retry that fails again leaves `error` set, which keeps this state up.
    await Promise.allSettled(erroredStates.map((key) => dataSet[key].refetch()));
    setRetrying(false);
  };

  const statusOptions = [
    { value: 'ALL', label: 'All' },
    ...(Object.keys(STAT_META) as ConsultationStatus[]).map((key) => {
      const total = getConsultationTotal(key);
      return {
        value: key,
        label:
          total === undefined
            ? STAT_META[key].label
            : `${STAT_META[key].label} (${total.toLocaleString()})`,
      };
    }),
    { value: 'NEEDS_REVIEW', label: 'Needs review' },
  ];

  const handleStatusChange = (value: string) => {
    const normalized = normalizeSpace(value);
    if (normalized === 'NEEDS_REVIEW') {
      if (!needsReview) toggleNeedsReview();
      return;
    }
    if (needsReview) {
      setSearchParams((prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        next.delete('filter');
        return next;
      });
    }
    setStatus(normalized);
  };

  return (
    <div>
      <div
        className="pt-3 sticky top-[52px] z-10 pb-5"
        style={{ backgroundColor: 'var(--surfaceBase)' }}
      >
        <div className="mb-16 flex flex-col gap-12 md:flex-row md:items-center md:justify-between md:gap-16">
          <h2
            className="m-0 text-[20px] font-semibold"
            style={{ color: 'var(--onSurface)', letterSpacing: '-0.01em' }}
          >
            Consultations
          </h2>
          <div className="flex flex-col gap-12 sm:flex-row sm:items-center sm:gap-16">
            <div className="w-full sm:w-[180px]">
              <Select
                sort={false}
                margin="dense"
                value={needsReview ? 'NEEDS_REVIEW' : status}
                fullWidth
                onChange={(event: any) => {
                  handleStatusChange(event.target.value);
                }}
                options={statusOptions}
              />
            </div>
            <div className="w-full sm:w-[280px]">
              <TextField
                value={searchTitle ?? ''}
                margin="none"
                fullWidth
                onChange={({ target: { value } }) => {
                  setSearchTitle(normalizeSpace(value));
                }}
                placeholder="Filter by project or expert request"
              />
            </div>
          </div>
        </div>

        {/* Filter chips + Add External Consultation: desktop only */}
        <div className="hidden items-center gap-12 md:flex md:flex-wrap">
          {(Object.keys(STAT_META) as ConsultationStatus[]).map((key) => {
            const meta = STAT_META[key];
            const total = getConsultationTotal(key);
            const isActive = status === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleStatusChange(isActive ? 'ALL' : key)}
                className={cx(
                  'text-sm flex shrink-0 items-center rounded-md border transition-colors',
                  isActive
                    ? 'bg-brand-primary/10 border-brand-primary'
                    : 'border-surface-border bg-surface-card hover:bg-surface-elevated'
                )}
                style={{ padding: '8px 16px', gap: 8 }}
              >
                <MaterialIcon icon={meta.icon} size={16} className={meta.accent} />
                {total !== undefined && (
                  <span className="font-semibold text-on-surface">{total.toLocaleString()}</span>
                )}
                <span className="text-on-surface-secondary">{meta.label}</span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={toggleNeedsReview}
            className={cx(
              'text-sm flex shrink-0 items-center rounded-md border transition-colors',
              needsReview
                ? 'border-yellow-500 bg-yellow-500/10'
                : 'border-surface-border bg-surface-card hover:bg-surface-elevated'
            )}
            style={{ padding: '8px 16px', gap: 8 }}
          >
            <MaterialIcon
              icon="rate_review"
              size={16}
              className="text-yellow-600 dark:text-yellow-500"
            />
            <span className="text-on-surface-secondary">Needs review</span>
          </button>
          {belongsToEnterpriseGroup && (
            <Button
              variant="text"
              submitting={erLoading}
              startIcon={<MaterialIcon icon="file_upload" />}
              onClick={handleExternalConsultationDialog}
              className="ml-auto"
            >
              Add External Consultation
            </Button>
          )}
        </div>
      </div>

      {belongsToEnterpriseGroup && (
        <div className="mb-12 flex md:hidden">
          <Button
            variant="contained"
            color="white"
            submitting={erLoading}
            startIcon={<MaterialIcon icon="file_upload" />}
            onClick={handleExternalConsultationDialog}
            className="w-full"
          >
            Add External Consultation
          </Button>
        </div>
      )}

      {/* Consultation lists */}
      {erroredStates.length > 0 && (
        <ConsultationPromo
          status="ERROR"
          action={
            <Button variant="text" submitting={retrying} onClick={handleRetry}>
              Try again
            </Button>
          }
        />
      )}
      {allConsultationsCount === 0 &&
        status === 'ALL' &&
        !allLoading &&
        erroredStates.length === 0 && <ConsultationPromo status={status} />}

      {Object.keys(dataSet).map((key) => {
        const { data, loading, error } = dataSet[key as ConsultationStatus];
        // Render off the rows actually shown, not the total: under needs-review the
        // loaded completed rows can all be filtered out while the total is non-zero.
        const shown = filterEdges(data?.searchConsultations?.edges) ?? [];
        const hasConsultations = shown.length > 0;
        // Needs-review filters loaded pages on the client, so reviewable rows can sit on
        // a page that has not loaded yet: say so and keep loading reachable.
        const hasMore = !!data?.searchConsultations?.pageInfo?.hasNextPage;
        const reviewQueue = needsReview && key === 'COMPLETED';
        const loadingMore = fetchingMore.includes(key as ConsultationStatus);

        return (
          <div key={key}>
            {!hasConsultations && status !== 'ALL' && status === key && !loading && !error && (
              <ConsultationPromo
                status={reviewQueue ? (hasMore ? 'NEEDS_REVIEW_MORE' : 'NEEDS_REVIEW') : key}
                action={
                  reviewQueue && hasMore ? (
                    <Button
                      variant="text"
                      submitting={loadingMore}
                      onClick={() => handleMore(key as ConsultationStatus)}
                    >
                      Load More
                    </Button>
                  ) : undefined
                }
              />
            )}
            {hasConsultations && (status === 'ALL' || status === key) && (
              <ConsultationList
                title={STATE_TO_TITLE_MAP[key]}
                consultations={shown.map((c: any) => c.node)}
                loading={loading || loadingMore}
                pageInfo={data.searchConsultations.pageInfo}
                handleMore={() => handleMore(key as ConsultationStatus)}
              />
            )}
          </div>
        );
      })}

      <EditExternalConsultation
        open={externalConsultationOpen}
        onClose={closeExternalConsultation}
      />
    </div>
  );
};

export default connector(Consultations);
